import BaseModel from '@core/model';

import ValuationOriginEnum from '@constants/enums/valuation/origin';
import ValuationTypeEnum from '@constants/enums/valuation/type';
import ValuationDispositionEnum from '@constants/enums/valuation/disposition';
import TaxStatusEnum from '@constants/enums/taxStatus';

import ProductApi from '@packages/products/api';

import User from '@/models/User';
import Product from '@/models/Product';
import Exchange from '@/models/Exchange';

export class Valuation extends BaseModel {
    static entity = 'valuations';

    static fields() {
        return {
            ...super.fields(),
            id: this.string(null).nullable(),
            name: this.string(null).nullable(),
            origin: this.enum(ValuationOriginEnum).nullable(),
            type: this.enum(ValuationTypeEnum).nullable(),
            description: this.string(null).nullable(),
            share_price: this.attr(null).nullable(),
            nav_price: this.attr(null).nullable(),
            revaluation_date: this.string(null).nullable(),
            disposition: this.enum(ValuationDispositionEnum).nullable(),
            product_id: this.string(null).nullable(),
            product: this.belongsTo(Product, 'product_id'),
            tax_status: this.enum(TaxStatusEnum).nullable(),
            exchange_id: this.string(null).nullable(),
            exchange: this.belongsTo(Exchange, 'exchange_id'),
            authorised_date: this.string(null).nullable(),
            authorised_by_id: this.string(null).nullable(),
            authorised_by: this.belongsTo(User, 'authorised_by_id'),
            effective_at: this.string(null).nullable(),
            created_at: this.string(null).nullable(),
            last_updated_at: this.string(null).nullable()
        };
    }

    get is_valid() {
        const required_fields = ['origin', 'type', 'share_price', 'effective_at'];

        if (this.tax_status === TaxStatusEnum.VCT) {
            required_fields.push('nav_price');
        }

        return required_fields.every(field => this[`${field}`]);
    }

    async $create() {
        this.$creating = true;

        return await this.constructor.$create(this.$toPayload()).finally(() => {
            this.$creating = false;
        });
    }

    static async $create(data = null) {
        if (!data) {
            throw new Error('Missing payload when creating valuation');
        }
        if (!data.product_id) {
            throw new Error('Missing product_id when creating valuation', data);
        }
        if (data.tax_status === TaxStatusEnum.VCT && !data.nav_price) {
            throw new Error('Missing nav_price when creating valuation', data);
        }

        const response = await new ProductApi().methods.postValuation(data);

        return this.findBy(response.data.id);
    }

    async $update(data = null) {
        this.$updating = true;

        data = data || this.$toPayload();

        this.$saving = Object.keys(data);

        return await this.constructor.$update(this.id, 'PATCH', data).finally(() => {
            this.$updating = false;
            this.$saving = [];
        });
    }

    // eslint-disable-next-line no-unused-vars
    static async $update(valuation_id, method = 'PATCH', data = null) {
        if (!valuation_id) {
            throw new Error('Missing valuation_id when updating valuation', data);
        }
        if (!data) {
            throw new Error('Missing payload when updating valuation', data);
        }
        if (!data.product_id) {
            throw new Error('Missing product_id when updating valuation', data);
        }

        const item = this.findBy(valuation_id);

        const tax_status = data.tax_status || item?.tax_status;
        if (!tax_status) {
            throw new Error('Missing tax_status when updating valuation', data);
        }

        const nav_price = data.nav_price || item?.nav_price;
        if (tax_status === TaxStatusEnum.VCT && (!nav_price || data.nav_price === null)) {
            throw new Error('Missing nav_price when updating valuation', data);
        }

        await new ProductApi().methods.patchValuation(valuation_id, data.product_id, data);

        return this.findBy(valuation_id);
    }

    async $remove() {
        this.$removing = true;

        return await this.constructor.$remove(this.product_id, this.id).finally(() => {
            this.$removing = false;
        });
    }

    static async $remove(valuation_id, product_id) {
        if (!valuation_id) {
            throw new Error('Missing valuation_id when deleting valuation');
        }

        if (!product_id) {
            const item = this.findBy(valuation_id);
            if (!item) {
                throw new Error('product_id not found in valuation cache when deleting valuation');
            }
            product_id = item.product_id;
        }

        if (!product_id) {
            throw new Error('product_id not found in valuation when deleting valuation');
        }

        return await new ProductApi().methods.removeValuation(valuation_id, product_id);
    }
}

export default Valuation;
